import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, BookOpen, Heart, Target, TrendingUp, BarChart3 } from 'lucide-react';

const STATS = [
  { Icon: Users,      label: 'Students to Benefit',    desc: 'Annual target for student support',        color: 'text-brand-indigo',  bg: 'bg-brand-indigo/10',  border: 'border-brand-indigo/18'  },
  { Icon: Award,      label: 'Excellence Awards',      desc: 'Academic achievement recognition',         color: 'text-brand-amber',   bg: 'bg-brand-amber/10',   border: 'border-brand-amber/18'   },
  { Icon: BookOpen,   label: 'Educational Investment', desc: 'Annual funding commitment goal',           color: 'text-brand-emerald', bg: 'bg-brand-emerald/10', border: 'border-brand-emerald/18' },
  { Icon: Heart,      label: 'Integrity',              desc: 'Commitment to ethical operations',         color: 'text-brand-rose',    bg: 'bg-brand-rose/10',    border: 'border-brand-rose/18'    },
  { Icon: Target,     label: 'Support Categories',     desc: 'Comprehensive educational assistance',     color: 'text-brand-indigo',  bg: 'bg-brand-indigo/10',  border: 'border-brand-indigo/18'  },
  { Icon: TrendingUp, label: 'Foundation Year',        desc: 'Starting our educational mission',         color: 'text-brand-emerald', bg: 'bg-brand-emerald/10', border: 'border-brand-emerald/18' },
];

const containerVar = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
};
const itemVar = {
  hidden:  { opacity: 0, y: 28, scale: 0.94 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const fadeUp = { hidden: { opacity: 0, y: -18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55 } } };

const StatCard = ({ Icon, label, desc, color, bg, border, idx }) => (
  <motion.div
    variants={itemVar}
    whileHover={{ y: -6, scale: 1.02 }}
    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    className={`glassmorphism-card border ${border} p-7 rounded-2xl
                group hover:shadow-xl transition-all duration-300 relative overflow-hidden card-shine`}
  >
    {/* Number badge */}
    <span className="absolute top-4 right-5 text-5xl font-black text-foreground/4 leading-none select-none">
      {String(idx + 1).padStart(2, '0')}
    </span>

    <div className={`inline-flex items-center justify-center w-13 h-13 rounded-2xl ${bg} mb-5
                     group-hover:scale-110 transition-transform duration-300`}>
      <Icon className={`h-6 w-6 ${color}`} />
    </div>

    <h3 className={`text-base font-bold ${color} mb-1.5`}>{label}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
  </motion.div>
);

const StatsSection = () => (
  <section className="py-20 md:py-28 bg-gradient-to-br from-brand-indigo/5 via-background to-brand-emerald/5 dot-pattern">
    <div className="container mx-auto px-4 sm:px-6">

      {/* Badge + Header */}
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="flex justify-center mb-4">
          <span className="section-badge">
            <BarChart3 className="h-3 w-3" />
            Strategic Goals
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-5">
          Our <span className="gradient-text">Impact Goals</span>
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          Strategic objectives and commitments as we establish comprehensive educational
          assistance programs throughout Srikakulam region.
        </p>
      </motion.div>

      <motion.div
        variants={containerVar}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.12 }}
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        {STATS.map((stat, idx) => (
          <StatCard key={stat.label} {...stat} idx={idx} />
        ))}
      </motion.div>

      <motion.p
        className="text-center mt-14 text-sm text-muted-foreground/70 max-w-2xl mx-auto leading-relaxed"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
      >
        These targets reflect our dedication to transparent, meaningful educational
        assistance. As our programs develop, these metrics will showcase real impact.
      </motion.p>
    </div>
  </section>
);

export default StatsSection;
